import { useRef, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import RiveComponent, { RiveAlignment, RiveFit } from '../components/RiveComponent'
import * as rive from '@rive-app/canvas'
import { goBack, openEliteLandingPage, onReady, trackEvent, updateAppStatusBar } from '../utils/appBridge'
import { fetchUsersWrap } from '../repository/misc'
import RenderHTML from '../components/ui-components/RenderHTML'
import { EventNames } from '../types/misc.types'
import { getBottomPadding, getTopPadding } from '../utils/sessionStorage'
import { isIOS } from '../utils/utils'

type RiveType = 'ANNUAL_WRAP_2025_ELITE' | 'ANNUAL_WRAP_2025_NON_ELITE'

export const WrappedRivePage = () => {
  const eventsListRef = useRef<Set<string>>(new Set())
  const onReadyLock = useRef<boolean>(false)
  const [searchParams] = useSearchParams()

  // Fetch users wrap data from backend, fallback to mock data
  const {
    data: apiData,
    isSuccess,
    isError
  } = useQuery({
    queryKey: ['usersWrap'],
    queryFn: fetchUsersWrap,
    retry: 1,
    refetchOnWindowFocus: false
  })

  // Use API data if available, otherwise fallback to mock data
  // const usersWrapData = useMemo(() => {
  //   return apiData
  // }, [apiData])

  // Flatten frameData array into textRunValues format
  // Line1_1, Line2_1... for first array, Line1_2, Line2_2... for second array, etc.
  let textRunValues: Record<string, string> = {}
  if (apiData?.frameData && Array.isArray(apiData.frameData)) {
    apiData.frameData.forEach((frame: any, index: number) => {
      const frameNumber = index + 1
      if (frame.line1) textRunValues[`Line1_${frameNumber}`] = frame.line1
      if (frame.line2) textRunValues[`Line2_${frameNumber}`] = frame.line2
      if (frame.line3) textRunValues[`Line3_${frameNumber}`] = frame.line3
      if (frame.line4) textRunValues[`Line4_${frameNumber}`] = frame.line4
      if (frame.line5) textRunValues[`Line5_${frameNumber}`] = frame.line5
    })
  }

  // Log the response for debugging
  useEffect(() => {
    if (apiData) {
      console.log('Users Wrap Data:', apiData)
      console.log('Flattened Text Run Values:', textRunValues)
    }
  }, [apiData, textRunValues])

  // Track screen load event with source from query params
  useEffect(() => {
    const source = searchParams.get('source') || undefined
    trackEvent({
      eventName: EventNames.ScreenLoaded,
      payload: {
        ScreenName: 'Wrapped',
        screenType: 'Wrapped',
        ...(source && { source })
      } as any
    })
  }, [searchParams])

  // Get riveType from API response data
  // API returns "ANNUAL_WRAP_2025_ELITE" or "ANNUAL_WRAP_2025_NON_ELITE"
  const apiRiveType = apiData?.riveType
  console.log('🔍 API riveType value:', apiRiveType)
  console.log('🔍 Full apiData:', apiData)

  let riveType: RiveType = 'ANNUAL_WRAP_2025_NON_ELITE'
  if (apiRiveType === 'ANNUAL_WRAP_2025_ELITE' || apiRiveType === 'ANNUAL_WRAP_2025_NON_ELITE') {
    console.log('✅ Detected riveType:', apiRiveType)
    riveType = apiRiveType as RiveType
  } else {
    // Default to NON-ELITE if riveType is not provided or unknown
    console.warn('⚠️ Unknown or missing riveType, defaulting to ANNUAL_WRAP_2025_NON_ELITE. Received:', apiRiveType)
  }

  // Determine which Rive file to load based on riveType
  const riveSrc = riveType === 'ANNUAL_WRAP_2025_ELITE' ? '/rives/Recap2025-Elite.riv' : '/rives/Recap2025-NonElite.riv'
  console.log('📁 Selected Rive file:', riveSrc, 'for riveType:', riveType)

  // Determine artboard name based on riveType
  const artboardName = riveType === 'ANNUAL_WRAP_2025_ELITE' ? 'Elite' : 'Non-Elite'

  const handleRiveEvent = (event: rive.Event) => {
    // Check if this is a RiveEvent and log its details
    if (event.type === rive.EventType.RiveEvent && event.data) {
      const riveEventData = event.data as rive.RiveEventPayload
      if (riveEventData && 'name' in riveEventData) {
        const eventName = riveEventData.name
        // Track unique event names
        if (!eventsListRef.current.has(eventName)) {
          eventsListRef.current.add(eventName)
          console.log('📋 New Event Found! All Events So Far:', Array.from(eventsListRef.current))
        }

        // Log the specific 'Event' event
        if (eventName === 'Storyend') {
          console.log('Event is getting logged')
        }

        // Handle events based on riveType
        if (riveType === 'ANNUAL_WRAP_2025_ELITE') {
          // If riveType is ANNUAL_WRAP_2025_ELITE and we receive a 'Finish' event, call goBack
          if (eventName === 'Finish') {
            console.log('Finish event received for ANNUAL_WRAP_2025_ELITE riveType, calling goBack()')
            trackEvent({ eventName: EventNames.ButtonTap, payload: { eventId: 'finishElite', ScreenName: 'Wrapped' } })
            goBack()
          }
        } else if (riveType === 'ANNUAL_WRAP_2025_NON_ELITE') {
          // If riveType is ANNUAL_WRAP_2025_NON_ELITE and we receive a 'Finish' event, call goBack
          if (eventName === 'Finish') {
            console.log('Finish event received for ANNUAL_WRAP_2025_NON_ELITE riveType, calling goBack()')
            trackEvent({
              eventName: EventNames.ButtonTap,
              payload: { eventId: 'finishNonElite', ScreenName: 'Wrapped' }
            })
            goBack()
          }
          // If riveType is ANNUAL_WRAP_2025_NON_ELITE and we receive a 'Get Elite' event, call openEliteLandingPage
          else if (eventName === 'Get Elite') {
            console.log(
              'Get Elite event received for ANNUAL_WRAP_2025_NON_ELITE riveType, calling openEliteLandingPage()'
            )
            trackEvent({ eventName: EventNames.ButtonTap, payload: { eventId: 'getElite', ScreenName: 'Wrapped' } })
            openEliteLandingPage()
          }
        }
      }
    }
  }

  const handlePageReady = () => {
    if (!onReadyLock.current) {
      onReadyLock.current = true
      onReady()
    }
  }

  useEffect(() => {
    setTimeout(() => {
      handlePageReady()
    }, 1000)
    updateAppStatusBar('000000', 'light')
  }, [])

  useEffect(() => {
    if (isSuccess || isError) setTimeout(() => handlePageReady(), 150)
  }, [isSuccess, isError])

  const handleRiveLoad = (riveInstance: rive.Rive) => {
    console.log('Rive instance loaded:', riveInstance)

    // Debug: Try to get artboard info
    try {
      // Log available artboards if possible
      console.log('Rive instance details:', {
        src: riveSrc,
        stateMachines: 'State Machine 1',
        textRunValuesKeys: Object.keys(textRunValues)
      })
    } catch (error) {
      console.error('Error getting Rive instance info:', error)
    }
  }

  const handleClose = () => {
    trackEvent({ eventName: EventNames.ButtonTap, payload: { eventId: 'close', ScreenName: 'Wrapped' } })
    goBack()
  }

  const closeButtonHTML = `
    <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
<circle cx="14" cy="14" r="14" fill="white"/>
<path d="M17.9579 8.62505C18.3484 8.23458 18.9815 8.23468 19.372 8.62505C19.7625 9.01557 19.7625 9.6486 19.372 10.0391L15.412 13.9981L19.372 17.9581C19.7624 18.3486 19.7625 18.9816 19.372 19.3721C18.9815 19.7626 18.3485 19.7626 17.9579 19.3721L13.998 15.4122L10.039 19.3721C9.64846 19.7625 9.01541 19.7626 8.62492 19.3721C8.2346 18.9816 8.23458 18.3485 8.62492 17.9581L12.5839 13.9981L8.62492 10.0391C8.2344 9.64858 8.2344 9.01557 8.62492 8.62505C9.01545 8.23452 9.64846 8.23452 10.039 8.62505L13.998 12.584L17.9579 8.62505Z" fill="#232323"/>
</svg>
  `

  return (
    <div className='relative flex flex-col items-center justify-center h-screen w-full bottom-0 bg-white'>
      {/* Close Button */}
      <div
        onClick={handleClose}
        style={{ top: `${getTopPadding() + 8}px` }}
        className='absolute right-4 z-50 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer'
        aria-label='Close'
      >
        <RenderHTML innerHTMLText={closeButtonHTML} />
        {/* <CloseButton onClose={handleClose} /> */}
      </div>

      <div style={{ paddingBottom: `${isIOS() ? 4 : getBottomPadding() + 4}px` }} className='w-full h-full'>
        {apiData && (
          <RiveComponent
            key={riveSrc}
            src={riveSrc}
            autoplay
            fit={RiveFit.COVER}
            alignment={RiveAlignment.BOTTOM_CENTER}
            stateMachines='State Machine 1'
            artboard={artboardName}
            onRiveEventReceived={handleRiveEvent}
            onLoad={handleRiveLoad}
            textRunValues={textRunValues}
          />
        )}
      </div>
    </div>
  )
}
